// Re-export from shared panel components with extended state for sites
import { usePanelState as useBasePanelState } from '../../../shared/components/panel';
import { useSearchParams } from 'react-router-dom';
import { useCallback } from 'react';

export interface SitePanelState {
  isOpen: boolean;
  type: string | null;
  id: string | null;
  siteId: string | null;
  plotId: string | null;
}

/**
 * Sites-specific version of usePanelState
 * Extends the base panel state with additional siteId and plotId parameters
 * for managing site-related panels with contextual information
 *
 * @example
 * ```tsx
 * const { isOpen, type, id, siteId, plotId, openPanel, closePanel } = useSitePanelState();
 *
 * // Open a plot panel with context
 * openPanel('plot', '123', '456', '789'); // type, id, siteId, plotId
 * ```
 */
export function useSitePanelState() {
  const base = useBasePanelState();
  const [searchParams] = useSearchParams();

  // Extend with site-specific parameters
  const siteId = searchParams.get('siteId');
  const plotId = searchParams.get('plotId');

  const extendedOpenPanel = useCallback(
    (type: string, id: string, siteId?: string, plotId?: string) => {
      const additionalParams: Record<string, string> = {};
      if (siteId !== undefined) {
        additionalParams.siteId = siteId;
      }
      if (plotId !== undefined) {
        additionalParams.plotId = plotId;
      }
      base.openPanel(type, id, additionalParams);
    },
    [base]
  );

  return {
    ...base,
    siteId,
    plotId,
    openPanel: extendedOpenPanel,
  };
}
